import * as React from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import { IconButton, ListItemIcon } from '@mui/material';
import * as Icon from '@mui/icons-material/';
import { ListItem, ListItemButton, ListItemText } from '@mui/material/';
import { menus } from '../../routes/Menus';
import { AppBar, Drawer, DrawerHeader, StyledBox } from './DashboardStyle';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Login, TrocarSenha as TrocarSenhaRota } from '../../routes/RoutesNames';
import { useEffect } from 'react';
import Version from '../../version.js'
import { useSessaoPing } from '../../hooks/useSessaoPing';
import useAuth from '../../hooks/useAuth';
import RequirePermissao from '../../context/permissao/RequirePermissao';
import { usePermissao } from '../../context/permissao/PermissaoProvider';
import { nomeDoUsuarioLogado, estaEmPrimeiroAcesso } from '../../shared/utils/helpers/usuarioDoToken';

export default function Dashboard() {
  /* [22/10] Mantém a sessão viva enquanto o sistema está aberto. Fica AQUI, e não no
     App, porque este componente só existe para quem está dentro: na tela de login não
     há sessão a manter, e bater sem token produziria um 401 e um redirecionamento em
     cima de quem já está no login. */
  useSessaoPing();

  const auth = useAuth();

  /* Lido do token, e nao do contexto: o currentUser so e preenchido no signIn e volta a
     null depois de um F5, entao o nome sumiria em toda recarga. Recalculado quando o
     usuario muda, para o logout e o login seguinte trocarem o rotulo. */
  /* [22/10] PRIMEIRO ACESSO: enquanto a senha for a temporaria, a pessoa so pode estar na
     tela que a troca. Isto aqui e o que a prende - o menu some e qualquer outra rota e
     devolvida para la.

     A marca vem do TOKEN, que e assinado: o navegador nao consegue apaga-la para escapar,
     e ela sobrevive ao F5. Se dependesse de estado em memoria, recarregar a pagina
     liberaria o sistema inteiro. */
  const primeiroAcesso = estaEmPrimeiroAcesso();


  const nomeUsuario = React.useMemo(
    () => nomeDoUsuarioLogado(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [auth.currentUser]
  );

  /* [22/10] O MENU MOSTRA SÓ O QUE O PAPEL TEM.
     Grupo (Cadastros, Financeiro...) não é permissão: ele aparece quando SOBRA pelo menos
     um filho. Dois níveis configuráveis permitiriam estados que ninguém sabe explicar -
     grupo marcado sem nenhum filho (menu vazio) e filho marcado sem o grupo (tem a
     permissão e não acha o caminho).
     Enquanto `telas` for null a lista sai VAZIA: é melhor o menu demorar um instante do
     que piscar mostrando o que a pessoa não tem. Ver PermissaoProvider. */
  const { podeVer, telas } = usePermissao();

  const menusVisiveis = React.useMemo(() => {
    // Em primeiro acesso nao ha menu: a unica coisa a fazer e cadastrar a senha.
    if (primeiroAcesso) return [];
    if (telas === null) return [];

    /* [22/10 - CORRIGIDO] Olha a telaChave DECLARADA no item, nao a rota.
       Antes eu deduzia a tela a partir de screenRoute, e onze entradas deste menu usam
       texto literal ('Produto', 'Marca', 'CategoriaFinanceira'...) em vez da constante de
       RoutesNames. Para essas, a busca nao achava nada e a regra "rota desconhecida passa"
       as deixava SEMPRE visiveis - foi o defeito relatado: tirar todas as permissoes do
       papel e Cadastros, Produtos e Ordem Servico continuarem no menu.

       E AQUI O DESCONHECIDO ESCONDE, ao contrario do guardiao de rota. Sao perguntas
       diferentes: no menu, mostrar o que a pessoa nao pode usar e o proprio defeito; no
       guardiao, barrar uma rota nova que alguem esqueceu de mapear trancaria todo mundo
       fora dela, inclusive o administrador. */
    const visivel = (chave?: string) => (chave ? podeVer(chave) : false);

    return menus
      .map((item) => {
        if (!item.subMenu) return visivel(item.telaChave) ? item : null;

        const filhos = item.subMenu.filter((sub: any) => visivel(sub.telaChave));

        // Grupo nao e permissao: aparece quando sobra pelo menos um filho.
        return filhos.length > 0 ? { ...item, subMenu: filhos } : null;
      })
      .filter(Boolean) as typeof menus;
  }, [telas, podeVer, primeiroAcesso]);

  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // Fica aqui, e nao junto do `primeiroAcesso` la em cima, porque depende de `navigate` e
  // `location` - e no JavaScript ler uma const antes da declaracao e erro, nao undefined.
  React.useEffect(() => {
    if (primeiroAcesso && location.pathname !== TrocarSenhaRota) {
      navigate(TrocarSenhaRota, { replace: true });
    }
  }, [primeiroAcesso, location.pathname, navigate]);

  const [open, setOpen] = React.useState<boolean>(false);
  const [screen, setScreen] = React.useState<string>(location.pathname);
  const [openMenus, setOpenMenus] = React.useState<{ [key: number]: boolean }>(
    {}
  );

  const toggleSubMenu = (id: number) => {
    setOpen(true);
    setOpenMenus((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleDrawerOpen = (): void => {
    setOpen(true);
  };

  const handleDrawerClose = (): void => {
    setOpenMenus({});
    setOpen(false);
  };

  const refresh = (): void => {
    window.location.reload();
  };

  const handleMenuItemClick = (path: string): void => setScreen(path);

  const handleUnderlineMenuItemClick = (path: string): void => {
    /* [22/10] O "Sair" NAO SAIA. Ele so navegava para a tela de login - nao limpava o
       token e nao avisava o servidor. Com a sessao unica isso virou um beco: a sessao
       continuava viva, e a pessoa era recusada ao tentar entrar de novo, inclusive de
       outra maquina, ate a expiracao por inatividade.

       O logout() do AuthContext existia desde sempre e NINGUEM o chamava - era codigo
       morto. Enquanto sair significava so "trocar de tela", ninguem notou.

       A navegacao vem DEPOIS do await: o logout avisa o servidor usando o token, e sair
       da tela antes disso pode desmontar o componente no meio da chamada.

       E o `return` impede o setScreen('Sair') de cair no efeito de navegacao logo abaixo,
       que tentaria abrir uma rota chamada "Sair" por cima da tela de login. */
    if (path === 'Sair') {
      auth.logout().finally(() => navigate(Login));
      return;
    }

    setScreen(path);
  };

  useEffect(() => {
    // Evita loop infinito verificando se já não está na rota
    if (location.pathname !== screen) {
      navigate(screen);
    }
  }, [screen]);

  // Este useEffect atualiza o MENU quando você navega por BOTÕES internos (Fechar, Salvar, etc)
  useEffect(() => {
    if (location.pathname !== screen) {
      setScreen(location.pathname);
    }
  }, [location]);

  return (
    <Box
      sx={{ 
        display: 'flex', 
        backgroundColor: '#F3F3F1',
        height: '100vh',
        overflow: 'hidden',
       }}
      style={{ width: '100vw' }}
    >
      <CssBaseline />
      <AppBar position='fixed'>
        <Toolbar>
          {/* <IconButton
            color='inherit'
            aria-label='open drawer'
            onClick={handleDrawerOpen}
            edge='start'
            sx={{ marginRight: 5, ...(open && { display: 'none' }) }}
          >
            <Icon.Menu />
          </IconButton> */}
          <Typography variant='h5' noWrap component='div'>
            <div style={{ display: 'flex', paddingTop: '7px' }}>
              <img
                src='/images/promatec.png'
                style={{ cursor: 'pointer', paddingBottom: 8 }}
                onClick={refresh}
                width={100}
                alt='Promatec'
              ></img>
            </div>
          </Typography>
        </Toolbar>
      </AppBar>
      <Drawer
        variant='permanent'
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: 300,
          height: '100vh',
          overflow:' hidden',
        }}
        open={open}
        onMouseEnter={() => handleDrawerOpen()}
        onMouseLeave={() => handleDrawerClose()}
        PaperProps={{
          className: 'custom-scrollbar',
          sx: {
            height: '100vh',
            position: 'absolute',
            display: 'flex',
            flexDirection: 'column', 
            justifyContent: 'space-between', 
            overflowY: 'hidden', 
          }
        }}
      >
        <DrawerHeader
          className={
            'flex justify-center align-middle items-center  w-full '
          }
        >
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === 'rtl' ? (
              <Icon.ChevronRight />
            ) : (
              <Icon.ChevronLeft style={{ color: '#F3F3F1' }} />
            )}
          </IconButton>
        </DrawerHeader>
        <Divider />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflowY: 'auto',
            overflowX: 'hidden',
            marginTop: 0,
          }}
        >
          <List>
            {menusVisiveis.map((item) => (
              <React.Fragment key={item.id}>
                <ListItem disablePadding sx={{ display: 'block' }}>
                  <ListItemButton
                    onClick={() =>
                      item.subMenu
                        ? toggleSubMenu(item.id)
                        : handleMenuItemClick(item.screenRoute)
                    }
                    sx={{ minHeight: 48, justifyContent: open ? 'initial' : 'center', px: 2.5 }}
                    title={item.screenName}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 3 : 'auto',
                        justifyContent: 'center',
                        color: '#F3F3F1',
                      }}
                    >
                      <item.icon />
                    </ListItemIcon>
                    <ListItemText
                      primary={item.screenName}
                      primaryTypographyProps={{
                        fontWeight: 'bold',
                        color: '#F3F3F1',
                      }}
                      sx={{ opacity: open ? 1 : 0 }}
                    />
                    {open &&
                      item.subMenu &&
                      (openMenus[item.id] ? (
                        <Icon.ExpandLess style={{ color: '#F3F3F1' }} />
                      ) : (
                        <Icon.ExpandMore style={{ color: '#F3F3F1' }} />
                      ))}
                  </ListItemButton>
                </ListItem>
                {item.subMenu && openMenus[item.id] && (
                  <List sx={{ pl: 4 }}>
                    {item.subMenu.map((sub) => (
                      <ListItem key={sub.id} disablePadding>
                        <ListItemButton
                          onClick={() => handleMenuItemClick(sub.screenRoute)}
                          sx={{
                            minHeight: 48,
                            justifyContent: 'center',
                            px: 2.5,
                          }}
                        >
                          <ListItemText
                            primary={sub.screenName}
                            primaryTypographyProps={{
                              fontWeight: 'bold',
                              color: '#F3F3F1',
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                )}
              </React.Fragment>
            ))}
          </List>
          {open && (
            <div
              style={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'end',
                padding: '8px',
                gap: '2px',
              }}
            >
              {/* [22/10] Quem está logado, ao lado da versão. Com papéis diferentes por
                  pessoa, "por que não vejo essa tela?" passa a depender de QUEM entrou -
                  e, em máquina compartilhada, ninguém tem certeza. O nome na tela
                  responde antes da pergunta. */}
              {nomeUsuario && (
                <span
                  style={{ color: '#F3F3F1', fontSize: '13px', fontWeight: 'bold' }}
                  title={nomeUsuario}
                >
                  {nomeUsuario}
                </span>
              )}
              <span style={{ fontFamily: 'monospace', color: '#BBB', fontSize: '14px' }}>
                v.{Version?.vApp}
              </span>
            </div>
          )}
        </Box>
        <Divider />
        <List className={''}>
          {[/*'Quem somos',*/ 'Sair'].map((text/*, index*/) => (
            <ListItem key={text} disablePadding sx={{ display: 'block' }}>
              <ListItemButton
                onClick={() => handleUnderlineMenuItemClick(text)}
                sx={{
                  minHeight: 48,
                  justifyContent: open ? 'initial' : 'center',
                  px: 2.5,
                }}
                title={text}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : 'auto',
                    justifyContent: 'center',
                    color: '#F3F3F1',
                  }}
                >
                  {/* {index % 2 === 0 ? <Icon.HomeWork /> : <Icon.Logout />} */}
                  <Icon.Logout />
                </ListItemIcon>
                <ListItemText
                  primary={text}
                  primaryTypographyProps={{
                    fontWeight: 'bold',
                    color: '#F3F3F1',
                  }}
                  sx={{ opacity: open ? 1 : 0 }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

      </Drawer>
      <StyledBox>
        {/* [22/10] O guardião fica em volta do Outlet, e não em cada uma das 85 rotas:
            ele lê o endereço atual, então reavalia sozinho a cada navegação. Embrulhar
            rota por rota seria 85 chances de esquecer uma - e a esquecida seria
            justamente a que ninguém testa. */}
        <RequirePermissao>
          <Outlet />
        </RequirePermissao>
      </StyledBox>
    </Box>
  );
}
